<template>
  <section class="i-outputs-section">
    <div class="container-wide">
      <div
        class="container"
        style="padding: 0"
      >
        <div class="label mb-3">What you'll walk away with</div>
        <h2 class="headline">
          Specific outputs.<br /><span style="color: var(--purple)">Not more things to think about.</span>
        </h2>
      </div>

      <div class="outputs-grid">
        <div
          v-for="item in OUTPUTS"
          :key="item.title"
          class="outputs-grid__item"
        >
          <div
            class="outputs-grid__icon"
            :class="`outputs-grid__icon--${item.color}`"
          >
            {{ item.icon }}
          </div>
          <div class="outputs-grid__text">
            <div class="outputs-grid__title">{{ item.title }}</div>
            <div class="outputs-grid__desc">{{ item.desc }}</div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
// CONSTANTS
const OUTPUTS = [
  {
    icon: "📋",
    color: "purple",
    title: "A clear interpretation of your results",
    desc: "What your profile means in the context of your background — not the generic definition on the page."
  },
  {
    icon: "🎯",
    color: "red",
    title: "3–5 specific role and industry targets",
    desc: "Actual job titles matched to your profile, with reasoning behind each so you can evaluate them intelligently."
  },
  {
    icon: "💰",
    color: "amber",
    title: "Your realistic salary ceiling",
    desc: "Current market benchmarks for your target roles. What you should be asking for — and how to get there."
  },
  {
    icon: "📍",
    color: "red",
    title: "A sequenced 30-90 day action plan",
    desc: "Specific steps, in order, starting with the highest-leverage moves. Delivered to your inbox within 24 hours."
  },
  {
    icon: "💬",
    color: "blue",
    title: "Your positioning language",
    desc: "Exactly how to describe your career transition in interviews. Language that lands — not language that confuses."
  },
  {
    icon: "📄",
    color: "indigo",
    title: "Written plan delivered within 24 hours",
    desc: "Everything discussed, written up and sent to your email — a concrete reference as you execute."
  }
]
</script>

<style lang="scss" scoped>
.i-outputs-section {
  background: var(--purple-pale);

  .outputs-grid {
    display: grid;
    margin-top: 36px;
    max-width: 760px;
    margin-left: auto;
    margin-right: auto;
    grid-template-columns: 1fr;
    gap: 10px;

    @include respond-to(680px) {
      grid-template-columns: 1fr 1fr;
      gap: 14px;
    }

    &__item {
      background: var(--white);
      border: 1px solid var(--border);
      border-radius: 14px;
      padding: 22px 20px;
      display: flex;
      align-items: flex-start;
      gap: 14px;
      transition: box-shadow 0.2s;

      &:hover {
        box-shadow: var(--card-shadow);
      }
    }

    &__icon {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 1.1rem;

      &--purple {
        background: var(--purple-pale);
      }

      &--amber {
        background: #fef3c7;
      }

      &--red {
        background: #fee2e2;
      }

      &--green {
        background: var(--green-lt);
      }

      &--blue {
        background: #dbeafe;
      }

      &--indigo {
        background: #e0e7ff;
      }
    }

    &__text {
      flex: 1;
      min-width: 0;
    }

    &__title {
      font-size: 0.9rem;
      font-weight: 700;
      color: var(--text);
      margin-bottom: 4px;
      line-height: 1.35;
    }

    &__desc {
      font-size: 0.8rem;
      color: var(--muted);
      line-height: 1.55;
    }
  }
}
</style>
